import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { trackFeature } from '../../utils/analytics';
import PreFlightCard from './PreFlightCard';
import { PLATFORMS } from '../../constants/platforms';
import { PLATFORM_TOS } from '../../constants/platformCompliance';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  ClockIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import YouTubeSettingsPanel from './YouTubeSettingsPanel';
import TikTokSettingsPanel from './TikTokSettingsPanel';
import InstagramSettingsPanel from './InstagramSettingsPanel';
import FacebookSettingsPanel from './FacebookSettingsPanel';
import TwitterSettingsPanel from './TwitterSettingsPanel';
import PlatformPreviewCard from './PlatformPreviewCard';

const PLATFORM_META = PLATFORMS.reduce((acc, p) => { acc[p.value] = p; return acc; }, {});

const STEP_LABELS = ['Platform Settings', 'Preview', 'Consent & Publish'];

const SETTINGS_PANELS = {
  YOUTUBE: YouTubeSettingsPanel,
  TIKTOK: TikTokSettingsPanel,
  INSTAGRAM: InstagramSettingsPanel,
  FACEBOOK: FacebookSettingsPanel,
  TWITTER: TwitterSettingsPanel,
};

const PublishSettingsModal = ({ contentId, platforms: initialPlatforms, content, fileDuration, onPublished, onClose, onDiscarded, onTargetsUpdated }) => {
  const [step, setStep] = useState(0); // 0=Settings, 1=Preview, 2=Consent, 3=Published
  // Platforms are editable mid-wizard (targets are mutable until publishing starts)
  const [platforms, setPlatforms] = useState(initialPlatforms);
  const [settings, setSettings] = useState({}); // { YOUTUBE: {...}, TIKTOK: {...}, ... }
  const [capabilities, setCapabilities] = useState({}); // { YOUTUBE: {...}, ... }
  const [capLoading, setCapLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null); // stores API response for post-publish step
  const [consent, setConsent] = useState({}); // { YOUTUBE: false, TIKTOK: false, ... }
  const [originalityCertified, setOriginalityCertified] = useState(false);
  // Unified scheduling: publish now, or hold for a future time. One control for every platform —
  // the backend holds the content and our dispatcher publishes it everywhere at the chosen time.
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(''); // datetime-local (viewer's local time)
  const [expanded, setExpanded] = useState(() => {
    return initialPlatforms.length > 0 ? { [initialPlatforms[0]]: true } : {};
  });
  const tikTokPollRef = useRef(null);
  const [tikTokPollStatus, setTikTokPollStatus] = useState(null); // 'processing' | 'complete' | 'failed'

  // In-wizard platform editing
  const [editingPlatforms, setEditingPlatforms] = useState(false);
  const [availableTargets, setAvailableTargets] = useState([]); // rows from compatible-platforms
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [pickerSelection, setPickerSelection] = useState(new Set()); // connectionIds
  const [savingTargets, setSavingTargets] = useState(false);

  // Exit-intent: explicit choice instead of silently abandoning the draft
  const [confirmExit, setConfirmExit] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Initialize/merge settings and consent for each platform.
  // Merging (not resetting) preserves entered settings when the platform set changes.
  useEffect(() => {
    setSettings(prev => {
      const next = {};
      platforms.forEach(p => {
        next[p] = prev[p] ?? (p === 'TIKTOK'
          // Point 2c: Interaction checkboxes unchecked by default = interactions disabled
          ? { disableComment: true, disableDuet: true, disableStitch: true }
          : {});
      });
      return next;
    });
    setConsent(prev => {
      const next = {};
      platforms.forEach(p => { next[p] = prev[p] ?? false; });
      return next;
    });
  }, [platforms]);

  const openPlatformEditor = async () => {
    setEditingPlatforms(true);
    setTargetsLoading(true);
    try {
      const res = await api.get('/api/v1/content/compatible-platforms', {
        params: { contentType: content?.contentType || 'VIDEO' },
      });
      // Payload shape: { connected: [...], allCompatible: [...], expired: [...] }
      const payload = res.data?.data || {};
      const rows = Array.isArray(payload.connected) ? payload.connected : [];
      setAvailableTargets(rows);
      // Preselect one connection per currently-targeted platform (prefer primary)
      const selection = new Set();
      platforms.forEach(p => {
        const matches = rows.filter(r => r.platform === p);
        const preferred = matches.find(r => r.primary === 'true' || r.primary === true) || matches[0];
        if (preferred) selection.add(preferred.connectionId);
      });
      setPickerSelection(selection);
    } catch {
      toast.error('Failed to load available platforms');
      setEditingPlatforms(false);
    } finally {
      setTargetsLoading(false);
    }
  };

  const togglePickerRow = (connectionId) => {
    setPickerSelection(prev => {
      const next = new Set(prev);
      if (next.has(connectionId)) next.delete(connectionId); else next.add(connectionId);
      return next;
    });
  };

  const saveTargets = async () => {
    const selectedRows = availableTargets.filter(r => pickerSelection.has(r.connectionId));
    const newPlatforms = [...new Set(selectedRows.map(r => r.platform))];
    if (newPlatforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    setSavingTargets(true);
    try {
      const res = await api.put(`/api/v1/content/${contentId}/targets`, {
        targetPlatforms: newPlatforms,
        targetConnectionIds: selectedRows.map(r => r.connectionId),
      });
      setPlatforms(newPlatforms);
      setExpanded(prev => ({ ...prev, [newPlatforms[0]]: prev[newPlatforms[0]] ?? true }));
      setEditingPlatforms(false);
      trackFeature('content_targets_updated');
      onTargetsUpdated?.(res.data?.data);
      toast.success(`Platforms updated — no re-upload needed`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update platforms');
    } finally {
      setSavingTargets(false);
    }
  };

  /** Close request pre-publish → ask the user what to do with the draft. */
  const requestClose = () => {
    if (publishing || discarding) return;
    if (step === 3) { onClose(); return; }
    setConfirmExit(true);
  };

  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      // Nothing persisted yet → just close; otherwise delete (backend delete is idempotent, so an
      // already-removed draft still resolves as discarded rather than "not found").
      if (contentId) {
        await api.delete(`/api/v1/content/${contentId}`);
      }
      toast.success('Upload discarded');
      if (onDiscarded) onDiscarded(contentId); else onClose();
    } catch (err) {
      // A 404 means it's already gone — treat that as discarded too.
      if (err.response?.status === 404) {
        if (onDiscarded) onDiscarded(contentId); else onClose();
        return;
      }
      toast.error(err.response?.data?.message || 'Failed to discard');
      setDiscarding(false);
    }
  };

  // Fetch platform capabilities on mount
  useEffect(() => {
    let cancelled = false;
    setCapLoading(true);
    api.get('/api/v1/content/publish-capabilities', {
      params: { platforms: platforms.join(',') },
    })
      .then(res => {
        if (cancelled) return;
        const caps = {};
        (res.data?.data || []).forEach(c => { caps[c.platform] = c; });
        setCapabilities(caps);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load platform capabilities');
      })
      .finally(() => { if (!cancelled) setCapLoading(false); });
    return () => { cancelled = true; };
  }, [platforms]);

  // Cleanup TikTok polling on unmount
  useEffect(() => {
    return () => {
      if (tikTokPollRef.current) clearInterval(tikTokPollRef.current);
    };
  }, []);

  // TikTok status polling after publish
  const pollTikTokStatus = useCallback(() => {
    if (!platforms.includes('TIKTOK')) return;
    setTikTokPollStatus('processing');

    let attempts = 0;
    const maxAttempts = 20; // 20 * 15s = 5 minutes
    tikTokPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(tikTokPollRef.current);
        tikTokPollRef.current = null;
        return;
      }
      try {
        const res = await api.get(`/api/v1/content/${contentId}/tiktok-status`);
        const status = res.data?.data?.status;
        if (status === 'PUBLISH_COMPLETE') {
          setTikTokPollStatus('complete');
          toast.success('TikTok video is now live!');
          clearInterval(tikTokPollRef.current);
          tikTokPollRef.current = null;
        } else if (status === 'FAILED') {
          setTikTokPollStatus('failed');
          toast.error('TikTok video processing failed');
          clearInterval(tikTokPollRef.current);
          tikTokPollRef.current = null;
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 15000);
  }, [contentId, platforms]);

  // Validation for step 0 (settings)
  const settingsValid = useMemo(() => {
    return platforms.every(p => {
      if (p === 'TIKTOK') {
        if (!settings[p]?.privacyLevel) return false;
        // Point 3a: Commercial toggle ON but no sub-option selected → invalid
        if (settings[p]?.commercialContentToggle && !settings[p]?.brandOrganic && !settings[p]?.brandContent) {
          return false;
        }
        // Point 1b: Posting restriction from creator_info
        if (capabilities[p]?.postingRestricted) return false;
      }
      if (p === 'TIKTOK' && fileDuration && capabilities[p]?.maxVideoPostDurationSec) {
        if (fileDuration > capabilities[p].maxVideoPostDurationSec) return false;
      }
      if (p === 'INSTAGRAM' && capabilities[p]?.instagramAccountType === 'PERSONAL') {
        return false;
      }
      return true;
    });
  }, [platforms, settings, capabilities, fileDuration]);

  // Tooltip for disabled Next button (Point 3a)
  const nextButtonTooltip = useMemo(() => {
    if (step !== 0) return '';
    for (const p of platforms) {
      if (p === 'TIKTOK') {
        if (!settings[p]?.privacyLevel) return 'You must select a TikTok privacy level';
        if (settings[p]?.commercialContentToggle && !settings[p]?.brandOrganic && !settings[p]?.brandContent) {
          return 'You need to indicate if your content promotes yourself, a third party, or both.';
        }
      }
    }
    return '';
  }, [step, platforms, settings]);

  const allConsented = useMemo(() =>
    platforms.every(p => consent[p]),
  [platforms, consent]);

  const canPublish = useMemo(() =>
    allConsented && originalityCertified,
  [allConsented, originalityCertified]);

  const toggleExpand = (platform) => {
    setExpanded(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const updateSettings = (platform, newSettings) => {
    setSettings(prev => ({ ...prev, [platform]: newSettings }));
  };

  const toggleConsent = (platform) => {
    setConsent(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const toggleAllConsent = () => {
    const allTrue = platforms.every(p => consent[p]);
    const next = {};
    platforms.forEach(p => { next[p] = !allTrue; });
    setConsent(next);
  };

  const handlePublish = async () => {
    // Validate the schedule time up front so we never send a past instant to the backend.
    let scheduledIso = null;
    if (scheduleLater) {
      if (!scheduledAt) { toast.error('Pick a date and time to schedule'); return; }
      scheduledIso = new Date(scheduledAt).toISOString();
      if (new Date(scheduledIso) <= new Date()) { toast.error('Scheduled time must be in the future'); return; }
    }
    setPublishing(true);
    try {
      const res = await api.post(`/api/v1/content/${contentId}/publish`, {
        platformSettings: settings,
        consentAccepted: true,
        originalityCertified: true,
        ...(scheduledIso ? { scheduledAt: scheduledIso } : {}),
      });
      const published = res.data.data;
      onPublished(published);

      // Scheduled: the content is held and published to every platform at the chosen time.
      if (scheduledIso) {
        trackFeature('content_schedule');
        toast.success(`Scheduled for ${new Date(scheduledAt).toLocaleString()}`);
        onClose();
        return;
      }
      trackFeature('content_publish_with_settings');

      const jobs = published.platformJobs || [];
      const succeeded = jobs.filter(j => j.status === 'COMPLETED');
      const failed = jobs.filter(j => j.status === 'FAILED');
      if (failed.length === 0) {
        toast.success(`Published to ${succeeded.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}`);
      } else if (succeeded.length > 0) {
        toast(`Published to ${succeeded.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}. Failed: ${failed.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}`, { icon: '\u26a0\ufe0f', duration: 6000 });
      } else {
        toast.error(`Failed to publish: ${failed.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}`, { duration: 6000 });
      }

      // Store result and show post-publish step
      setPublishResult(published);
      setStep(3);

      // Start TikTok status polling in background
      if (platforms.includes('TIKTOK')) {
        pollTikTokStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish content');
    } finally {
      setPublishing(false);
    }
  };

  const durationWarning = useMemo(() => {
    if (!fileDuration || !platforms.includes('TIKTOK')) return null;
    const maxSec = capabilities.TIKTOK?.maxVideoPostDurationSec;
    if (maxSec && fileDuration > maxSec) {
      return `Video duration (${Math.round(fileDuration)}s) exceeds TikTok limit (${maxSec}s)`;
    }
    return null;
  }, [fileDuration, capabilities, platforms]);

  // Extract TikTok publish info from result
  const tikTokJob = publishResult?.platformJobs?.find(j => j.platform === 'TIKTOK');
  const tikTokPublishId = tikTokJob?.platformContentId || tikTokJob?.publishId;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => !publishing && step !== 3 && requestClose()}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
        className="relative bg-white dark:bg-dark-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {step === 3 ? 'Published!' : 'Publish Settings'}
            </h2>
            {step < 3 && (
              <div className="flex items-center gap-2 mt-1">
                {STEP_LABELS.map((label, i) => (
                  <div key={label} className="flex items-center gap-1">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      i < step
                        ? 'bg-green-500 text-white'
                        : i === step
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {i < step ? <CheckIcon className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className={`text-xs ${i === step ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400'}`}>
                      {label}
                    </span>
                    {i < STEP_LABELS.length - 1 && (
                      <div className="w-6 h-px bg-gray-300 dark:bg-dark-600 mx-1" />
                    )}
                  </div>
                ))}
              </div>
            )}
            {step === 3 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your content has been submitted to the selected platforms.
              </p>
            )}
          </div>
          <button
            onClick={step === 3 ? onClose : requestClose}
            disabled={publishing}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {durationWarning && step < 3 && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{durationWarning}</p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {/* Target platforms — editable until publishing starts, no re-upload needed */}
                <div className="mb-1 p-3 rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50/60 dark:bg-dark-800/40">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Publishing to:</span>
                      {platforms.map(p => {
                        const meta = PLATFORM_META[p] || {};
                        const Icon = meta.icon;
                        return (
                          <span key={p} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.bgColor || 'bg-gray-100 dark:bg-dark-700'} ${meta.color || 'text-gray-600'}`}>
                            {Icon && <Icon className="text-xs" />}
                            {meta.label || p}
                          </span>
                        );
                      })}
                    </div>
                    {!editingPlatforms && (
                      <button
                        type="button"
                        onClick={openPlatformEditor}
                        className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 whitespace-nowrap"
                      >
                        ✎ Edit platforms
                      </button>
                    )}
                  </div>

                  {editingPlatforms && (
                    <div className="mt-3 border-t border-gray-200 dark:border-dark-700 pt-3">
                      {targetsLoading ? (
                        <div className="flex items-center py-3 text-sm text-gray-500">
                          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2" />
                          Loading connected platforms…
                        </div>
                      ) : (
                        <>
                          <div className="space-y-1.5 max-h-44 overflow-y-auto">
                            {availableTargets.length === 0 && (
                              <p className="text-xs text-gray-500 py-2">No compatible connected platforms found.</p>
                            )}
                            {availableTargets.map(row => {
                              const meta = PLATFORM_META[row.platform] || {};
                              const Icon = meta.icon;
                              const checked = pickerSelection.has(row.connectionId);
                              return (
                                <label
                                  key={row.connectionId}
                                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                                    checked
                                      ? 'border-primary-300 bg-primary-50/60 dark:border-primary-700 dark:bg-primary-900/15'
                                      : 'border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-800'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePickerRow(row.connectionId)}
                                    className="rounded text-primary-600 focus:ring-primary-500"
                                  />
                                  {Icon && <Icon className={`w-4 h-4 ${meta.color || 'text-gray-600'}`} />}
                                  <span className="text-sm text-gray-800 dark:text-gray-200">{meta.label || row.platform}</span>
                                  {(row.displayName || row.platformUsername) && (
                                    <span className="text-xs text-gray-400 truncate">
                                      {row.displayName || row.platformUsername}
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <button
                              type="button"
                              onClick={() => setEditingPlatforms(false)}
                              disabled={savingTargets}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={saveTargets}
                              disabled={savingTargets || pickerSelection.size === 0}
                              className="px-3.5 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {savingTargets && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                              Save platforms
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {capLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-3 text-sm text-gray-500">Loading platform settings...</span>
                  </div>
                ) : (
                  platforms.map((platform, idx) => {
                    const meta = PLATFORM_META[platform] || {};
                    const Icon = meta.icon;
                    const Panel = SETTINGS_PANELS[platform];
                    const isExpanded = expanded[platform];
                    const isIgPersonalBlocked = platform === 'INSTAGRAM'
                      && capabilities[platform]?.instagramAccountType === 'PERSONAL';

                    return (
                      <div
                        key={`${platform}-${idx}`}
                        className={`border rounded-xl overflow-hidden transition-colors ${
                          isExpanded
                            ? `border-l-4 ${meta.borderColor || 'border-gray-300'}`
                            : 'border-gray-200 dark:border-dark-700'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleExpand(platform)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {Icon && <Icon className={`w-5 h-5 ${meta.color || 'text-gray-600'}`} />}
                            <span className="font-medium text-gray-900 dark:text-white">{meta.label || platform}</span>
                            {platform === 'TIKTOK' && !settings[platform]?.privacyLevel && (
                              <span className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                                Privacy required
                              </span>
                            )}
                            {isIgPersonalBlocked && (
                              <span className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
                                Publishing disabled
                              </span>
                            )}
                          </div>
                          {isExpanded
                            ? <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                            : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                        </button>
                        <AnimatePresence>
                          {isExpanded && Panel && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4">
                                {isIgPersonalBlocked && (
                                  <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-xs text-red-700 dark:text-red-300 font-medium">
                                    Personal Instagram accounts cannot publish via the API.
                                    Switch to a Business or Creator account to enable publishing.
                                  </div>
                                )}
                                <Panel
                                  settings={settings[platform] || {}}
                                  onChange={s => updateSettings(platform, s)}
                                  capabilities={capabilities[platform] || {}}
                                  content={content}
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                )}
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Review how your content will appear on each platform.
                </p>
                {platforms.map((platform, idx) => (
                  <PlatformPreviewCard
                    key={`${platform}-${idx}`}
                    platform={platform}
                    content={content}
                    settings={settings[platform] || {}}
                  />
                ))}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="consent"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium mb-1">Review before publishing</p>
                    <p>Your content will be published to the selected platforms. Please confirm you agree to each platform&apos;s Terms of Service.</p>
                  </div>
                </div>

                {/* When to publish — one scheduling control for every selected platform */}
                <div className="rounded-lg border border-gray-200 dark:border-dark-700 p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">When to publish</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setScheduleLater(false)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${!scheduleLater
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'}`}
                    >
                      Publish now
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleLater(true)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${scheduleLater
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'}`}
                    >
                      Schedule for later
                    </button>
                  </div>
                  {scheduleLater && (
                    <div className="mt-3">
                      <input
                        type="datetime-local"
                        className="input w-full"
                        value={scheduledAt}
                        min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                        onChange={e => setScheduledAt(e.target.value)}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Held until this time, then published to all selected platforms at once (your local time).
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {platforms.map((platform, idx) => {
                    const meta = PLATFORM_META[platform] || {};
                    const Icon = meta.icon;
                    const tosUrl = PLATFORM_TOS[platform];
                    return (
                      <label
                        key={`${platform}-${idx}`}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={consent[platform] || false}
                          onChange={() => toggleConsent(platform)}
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {Icon && <Icon className={`w-4 h-4 ${meta.color || 'text-gray-600'}`} />}
                          <span className="text-sm text-gray-900 dark:text-white">{meta.label || platform}</span>
                        </div>
                        {tosUrl && (
                          <a
                            href={tosUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                          >
                            Terms of Service
                          </a>
                        )}
                      </label>
                    );
                  })}
                </div>

                {platforms.length > 1 && (
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-800 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={allConsented}
                      onChange={toggleAllConsent}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      I accept all platform Terms of Service
                    </span>
                  </label>
                )}

                {/* Content Originality Attestation */}
                <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <div className="flex items-start gap-3">
                    <ShieldCheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-2">Content Originality Attestation</p>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={originalityCertified}
                          onChange={() => setOriginalityCertified(prev => !prev)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          I confirm that this content is my original creation or that I have obtained all necessary rights, licenses, and permissions to publish it. This content does not infringe upon any third-party copyrights, trademarks, or other intellectual property rights.
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                        This attestation is recorded for legal and compliance purposes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* P2: advisory DNA score for this draft — flag-gated, never blocks publishing */}
                <PreFlightCard contentId={contentId} platforms={platforms} />
              </motion.div>
            )}

            {/* Point 5: Post-Publish Step */}
            {step === 3 && (
              <motion.div
                key="published"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                {/* Success summary */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">Content submitted successfully</p>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                      Your content has been sent to the selected platforms for publishing.
                    </p>
                  </div>
                </div>

                {/* Per-platform results */}
                <div className="space-y-2">
                  {(publishResult?.platformJobs || []).map((job, idx) => {
                    const meta = PLATFORM_META[job.platform] || {};
                    const Icon = meta.icon;
                    const isSuccess = job.status === 'COMPLETED';
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          isSuccess
                            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                            : 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                        }`}
                      >
                        {Icon && <Icon className={`w-5 h-5 ${meta.color || 'text-gray-600'}`} />}
                        <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">
                          {meta.label || job.platform}
                        </span>
                        {isSuccess ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                            <CheckCircleIcon className="w-4 h-4" /> Published
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                            <XCircleIcon className="w-4 h-4" /> Failed
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Point 5a + 5d: TikTok post-publish info */}
                {platforms.includes('TIKTOK') && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                    <div className="flex items-start gap-3">
                      <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          TikTok Content Processing
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Your TikTok content is being processed. It may take a few minutes before it appears on your profile.
                        </p>

                        {/* Processing status indicator */}
                        <div className="mt-3 flex items-center gap-2">
                          {tikTokPollStatus === 'processing' && (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              <span className="text-xs text-blue-600 dark:text-blue-400">Processing...</span>
                            </>
                          )}
                          {tikTokPollStatus === 'complete' && (
                            <>
                              <CheckCircleIcon className="w-4 h-4 text-green-500" />
                              <span className="text-xs text-green-600 dark:text-green-400">Published and live on TikTok</span>
                            </>
                          )}
                          {tikTokPollStatus === 'failed' && (
                            <>
                              <XCircleIcon className="w-4 h-4 text-red-500" />
                              <span className="text-xs text-red-600 dark:text-red-400">TikTok processing failed</span>
                            </>
                          )}
                        </div>

                        {/* Point 5a: Publish ID */}
                        {tikTokPublishId && (
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                            <span className="font-medium">Publish ID:</span>{' '}
                            <code className="bg-blue-100 dark:bg-blue-800/40 px-1.5 py-0.5 rounded font-mono">
                              {tikTokPublishId}
                            </code>
                          </div>
                        )}

                        {/* Point 5b: Video link if available */}
                        {tikTokJob?.platformUrl && (
                          <div className="mt-2">
                            <a
                              href={tikTokJob.platformUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 underline hover:text-blue-800"
                            >
                              View on TikTok
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* GAP-2: Instagram post-publish confirmation with a link to the live post.
                    Turns the "Published" status badge into verifiable end-to-end proof for App Review. */}
                {(() => {
                  const igJob = (publishResult?.platformJobs || []).find(
                    (j) => j.platform === 'INSTAGRAM' && j.status === 'COMPLETED'
                  );
                  if (!igJob) return null;
                  return (
                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <div className="flex items-start gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                            Posted to Instagram
                          </p>
                          <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                            Your content is now live on Instagram.
                          </p>
                          {igJob.platformUrl && (
                            <div className="mt-2">
                              <a
                                href={igJob.platformUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-purple-600 dark:text-purple-400 underline hover:text-purple-800"
                              >
                                View on Instagram
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-dark-700">
          {step === 3 ? (
            <>
              <div />
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-primary-600
                  hover:bg-primary-700 rounded-lg transition-colors"
              >
                <CheckIcon className="w-4 h-4" />
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => step === 0 ? requestClose() : setStep(s => s - 1)}
                disabled={publishing}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors disabled:opacity-50"
              >
                {step > 0 && <ArrowLeftIcon className="w-4 h-4" />}
                {step === 0 ? 'Cancel' : 'Back'}
              </button>

              {step < 2 ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={step === 0 && !settingsValid}
                  title={step === 0 && !settingsValid ? nextButtonTooltip : undefined}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-primary-600
                    hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  disabled={!canPublish || publishing || (scheduleLater && !scheduledAt)}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-green-600
                    hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {publishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {scheduleLater ? 'Scheduling...' : 'Publishing...'}
                    </>
                  ) : scheduleLater ? (
                    <>
                      <ClockIcon className="w-4 h-4" />
                      Schedule
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Publish to {platforms.length} platform{platforms.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* Exit-intent: explicit choice instead of silently abandoning the draft */}
        <AnimatePresence>
          {confirmExit && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-6"
              onClick={e => e.stopPropagation()}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm"
              >
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Save your progress?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                  {content?.contentType === 'TEXT'
                    ? 'Your draft is saved and can be resumed anytime from the content list.'
                    : 'Your upload is saved. Resume anytime from the content list — no re-upload needed.'}
                </p>
                <div className="mt-5 space-y-2">
                  <button
                    onClick={() => { setConfirmExit(false); onClose(); }}
                    disabled={discarding}
                    className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
                  >
                    Save as draft
                  </button>
                  <button
                    onClick={handleDiscard}
                    disabled={discarding}
                    className="w-full px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/35 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {discarding && <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />}
                    Discard upload
                  </button>
                  <button
                    onClick={() => setConfirmExit(false)}
                    disabled={discarding}
                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors"
                  >
                    Keep editing
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default PublishSettingsModal;
